// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Shared mock data for SOP pages.
 *
 * This file is the single source of truth for SOP demo data
 * until real API integration replaces it.
 */

import type { SOP, SOPVersionDiff } from '../../types';

// ---------------------------------------------------------------------------
// SOPs
// ---------------------------------------------------------------------------

export const MOCK_SOPS: SOP[] = [
  {
    id: 'sop-cooling-opt',
    name: 'Cooling Optimization Protocol',
    version: 'v2.3',
    status: 'Active',
    lastUpdated: 'Jan 15, 2026',
    updatedBy: 'J. Chen',
    boundsSummary: 'Temp 65\u201378\u00b0F \u00b7 Redundancy N+1',
    overview:
      'Defines operating parameters for pre-cooling the facility ahead of peak demand windows. Covers chiller setpoints, cooling tower sequencing, and thermal storage dispatch.',
    safetyBoundaries: [
      { parameter: 'Zone Temperature', min: '65', max: '78', unit: '\u00b0F' },
      { parameter: 'Supply Air Temp', min: '52', max: '58', unit: '\u00b0F' },
      { parameter: 'Cooling Redundancy', min: 'N+1', max: '\u2014', unit: '\u2014' },
      { parameter: 'Cooling Load', min: '\u2014', max: '85%', unit: 'capacity' },
    ],
    equipmentConfig: [
      {
        equipment: 'Chiller-1',
        parameter: 'Setpoint',
        normal: '72\u00b0F',
        optimized: '68\u00b0F',
      },
      {
        equipment: 'Chiller-2',
        parameter: 'Setpoint',
        normal: '72\u00b0F',
        optimized: '68\u00b0F',
      },
      { equipment: 'Cooling Tower', parameter: 'Fan Speed', normal: '60%', optimized: '85%' },
      { equipment: 'TES Tank', parameter: 'Charge Mode', normal: 'Idle', optimized: 'Discharge' },
    ],
    preChecks: [
      'All chillers reporting normal status',
      'Cooling tower water level > 80%',
      'No active fault alarms on cooling equipment',
      'TES tank state-of-charge > 70%',
      'Outside air temp confirmed from forecast',
    ],
    postChecks: [
      'All zone temps within safety boundaries',
      'No fault alarms triggered during execution',
      'Cooling redundancy maintained (N+1)',
      'Equipment returned to normal setpoints',
    ],
    rollbackInstructions: [
      'Return chiller setpoints to 72\u00b0F',
      'Reduce cooling tower fan speed to 60%',
      'Switch TES tank to Idle',
      'Notify on-duty operator via alert system',
      'Log rollback event with failure reason',
    ],
    author: 'J. Chen',
    createdDate: 'Oct 3, 2025',
    linkedExecutionCount: 12,
    lastUsed: 'Feb 28, 2026',
    approvedBy: 'M. Park',
    approvalDate: 'Jan 16, 2026',
    versions: [
      {
        id: 'v2.3',
        versionNumber: 'v2.3',
        status: 'Active',
        date: 'Jan 15, 2026',
        author: 'J. Chen',
        changeReason: 'Updated temperature bounds per Q1 review',
        approver: 'M. Park',
        approvalDate: 'Jan 16, 2026',
        executionCount: 3,
      },
      {
        id: 'v2.2',
        versionNumber: 'v2.2',
        status: 'Superseded',
        date: 'Dec 1, 2025',
        author: 'A. Williams',
        changeReason: 'Added TES tank rollback step',
        approver: 'J. Chen',
        approvalDate: 'Dec 2, 2025',
        executionCount: 5,
      },
      {
        id: 'v2.1',
        versionNumber: 'v2.1',
        status: 'Superseded',
        date: 'Oct 15, 2025',
        author: 'J. Chen',
        changeReason: 'Initial cooling tower sequencing logic',
        approver: 'M. Park',
        approvalDate: 'Oct 16, 2025',
        executionCount: 4,
      },
      {
        id: 'v2.0',
        versionNumber: 'v2.0',
        status: 'Superseded',
        date: 'Aug 3, 2025',
        author: 'M. Park',
        changeReason: 'Major revision: added equipment config section',
        approver: 'J. Chen',
        approvalDate: 'Aug 5, 2025',
        executionCount: 0,
      },
      {
        id: 'v1.2',
        versionNumber: 'v1.2',
        status: 'Superseded',
        date: 'Jun 12, 2025',
        author: 'J. Chen',
        changeReason: 'Adjusted supply air temp range',
        approver: 'M. Park',
        approvalDate: 'Jun 13, 2025',
        executionCount: 6,
      },
      {
        id: 'v1.1',
        versionNumber: 'v1.1',
        status: 'Superseded',
        date: 'Apr 20, 2025',
        author: 'A. Williams',
        changeReason: 'Added pre-check for outside air temp',
        approver: 'J. Chen',
        approvalDate: 'Apr 21, 2025',
        executionCount: 3,
      },
      {
        id: 'v1.0',
        versionNumber: 'v1.0',
        status: 'Superseded',
        date: 'Mar 1, 2025',
        author: 'J. Chen',
        changeReason: 'Initial version',
        approver: 'M. Park',
        approvalDate: 'Mar 2, 2025',
        executionCount: 2,
      },
    ],
  },
  {
    id: 'sop-battery-discharge',
    name: 'Battery Discharge Procedure',
    version: 'v1.1',
    status: 'Active',
    lastUpdated: 'Dec 3, 2025',
    updatedBy: 'M. Park',
    boundsSummary: 'SoC > 20% \u00b7 Max discharge 4MW',
    overview:
      'Governs battery energy storage discharge operations during peak pricing windows. Specifies state-of-charge thresholds, discharge rate limits, and grid fallback requirements.',
    safetyBoundaries: [
      { parameter: 'State of Charge', min: '20%', max: '\u2014', unit: '%' },
      { parameter: 'Discharge Rate', min: '\u2014', max: '4', unit: 'MW' },
      { parameter: 'Grid Backup', min: 'Required', max: '\u2014', unit: '\u2014' },
    ],
    equipmentConfig: [
      { equipment: 'BESS Unit A', parameter: 'Discharge Rate', normal: '0 MW', optimized: '2 MW' },
      { equipment: 'BESS Unit A', parameter: 'Mode', normal: 'Standby', optimized: 'Discharge' },
    ],
    preChecks: [
      'BESS state-of-charge > 70%',
      'Grid connection stable',
      'No active BESS fault alarms',
      'Discharge window within TOU peak period',
    ],
    postChecks: [
      'BESS returned to standby mode',
      'State-of-charge above minimum threshold',
      'No fault alarms triggered',
    ],
    rollbackInstructions: [
      'Switch BESS to standby mode immediately',
      'Verify grid connection is providing full load',
      'Notify on-duty operator',
      'Log rollback event',
    ],
    author: 'M. Park',
    createdDate: 'Sep 15, 2025',
    linkedExecutionCount: 8,
    lastUsed: 'Feb 21, 2026',
    approvedBy: 'J. Chen',
    approvalDate: 'Dec 4, 2025',
    versions: [
      {
        id: 'v1.1',
        versionNumber: 'v1.1',
        status: 'Active',
        date: 'Dec 3, 2025',
        author: 'M. Park',
        changeReason: 'Added grid backup requirement',
        approver: 'J. Chen',
        approvalDate: 'Dec 4, 2025',
        executionCount: 5,
      },
      {
        id: 'v1.0',
        versionNumber: 'v1.0',
        status: 'Superseded',
        date: 'Sep 15, 2025',
        author: 'M. Park',
        changeReason: 'Initial version',
        approver: 'J. Chen',
        approvalDate: 'Sep 16, 2025',
        executionCount: 3,
      },
    ],
  },
  {
    id: 'sop-emergency-load-shed',
    name: 'Emergency Load Shed Protocol',
    version: 'v3.0',
    status: 'Draft',
    lastUpdated: 'Feb 28, 2026',
    updatedBy: 'A. Williams',
    boundsSummary: 'Critical load priority list',
    overview:
      'Defines the load shedding sequence for emergency scenarios. Prioritizes critical IT loads and establishes the order in which non-critical systems are taken offline.',
    safetyBoundaries: [
      { parameter: 'Critical Load', min: '100%', max: '\u2014', unit: 'maintained' },
      { parameter: 'UPS Battery', min: '30 min', max: '\u2014', unit: 'runtime' },
    ],
    equipmentConfig: [
      {
        equipment: 'PDU-A1',
        parameter: 'Load Priority',
        normal: 'Normal',
        optimized: 'Critical Only',
      },
      { equipment: 'HVAC Zone B', parameter: 'Mode', normal: 'Active', optimized: 'Standby' },
    ],
    preChecks: [
      'UPS battery runtime > 30 minutes',
      'Generator fuel level > 50%',
      'Critical load circuits identified',
    ],
    postChecks: [
      'All critical loads maintained throughout',
      'Non-critical loads restored in priority order',
      'Generator returned to standby',
    ],
    rollbackInstructions: [
      'Restore all loads to normal priority',
      'Verify UPS charging status',
      'Run post-event diagnostics on all PDUs',
    ],
    author: 'A. Williams',
    createdDate: 'May 10, 2025',
    linkedExecutionCount: 2,
    lastUsed: 'Jan 5, 2026',
    approvedBy: '\u2014',
    approvalDate: '\u2014',
    versions: [
      {
        id: 'v3.0',
        versionNumber: 'v3.0',
        status: 'Draft',
        date: 'Feb 28, 2026',
        author: 'A. Williams',
        changeReason: 'Major revision of load priority sequence',
        approver: '\u2014',
        approvalDate: '\u2014',
        executionCount: 0,
      },
    ],
  },
  {
    id: 'sop-peak-demand',
    name: 'Peak Demand Response Procedure',
    version: 'v1.0',
    status: 'Archived',
    lastUpdated: 'Nov 20, 2025',
    updatedBy: 'J. Chen',
    boundsSummary: 'Load < 85% capacity',
    overview:
      'Legacy procedure for manual demand response during peak periods. Superseded by the Cooling Optimization Protocol and Battery Discharge Procedure combination.',
    safetyBoundaries: [{ parameter: 'Total Load', min: '\u2014', max: '85%', unit: 'capacity' }],
    equipmentConfig: [
      {
        equipment: 'All Chillers',
        parameter: 'Setpoint',
        normal: '72\u00b0F',
        optimized: '70\u00b0F',
      },
    ],
    preChecks: ['Total facility load below 90% capacity', 'No active maintenance windows'],
    postChecks: ['Load returned to normal levels', 'No thermal excursions'],
    rollbackInstructions: ['Return all setpoints to normal', 'Notify facility manager'],
    author: 'J. Chen',
    createdDate: 'Jul 1, 2025',
    linkedExecutionCount: 4,
    lastUsed: 'Nov 15, 2025',
    approvedBy: 'M. Park',
    approvalDate: 'Jul 2, 2025',
    versions: [
      {
        id: 'v1.0',
        versionNumber: 'v1.0',
        status: 'Archived',
        date: 'Jul 1, 2025',
        author: 'J. Chen',
        changeReason: 'Initial version',
        approver: 'M. Park',
        approvalDate: 'Jul 2, 2025',
        executionCount: 4,
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// SOP Version Diffs
// ---------------------------------------------------------------------------

export const MOCK_SOP_VERSION_DIFFS: SOPVersionDiff[] = [
  {
    versionId: 'v2.3',
    previousVersion: 'v2.2',
    sections: [
      {
        title: 'Safety Boundaries',
        changes: [
          {
            label: 'Zone Temperature Max',
            removed: '80\u00b0F',
            added: '78\u00b0F',
            type: 'modified',
          },
          {
            label: 'Supply Air Temp Min',
            removed: '50\u00b0F',
            added: '52\u00b0F',
            type: 'modified',
          },
        ],
      },
      {
        title: 'Post-Execution Checks',
        changes: [
          {
            label: 'Equipment returned to normal setpoints',
            added: 'Equipment returned to normal setpoints',
            type: 'added',
          },
        ],
      },
    ],
  },
  {
    versionId: 'v2.2',
    previousVersion: 'v2.1',
    sections: [
      {
        title: 'Rollback Instructions',
        changes: [
          {
            label: 'Log rollback event with failure reason',
            added: 'Log rollback event with failure reason',
            type: 'added',
          },
        ],
      },
    ],
  },
];
